"use client";

import { MessageSquare } from "lucide-react";
import Link from "next/link";
import { useCallback, useState } from "react";

import { ContextViewer } from "@/components/recruiter/context-viewer";
import { RatingIcons } from "@/components/shortlists/rating-icons";
import type {
  ConnectionState,
  ShortlistCandidate,
} from "@/hooks/use-recruiter-chat";
import type { AgentContext } from "@/lib/api/recruiter";
import { updateItem } from "@/lib/api/shortlists";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ShortlistPanelProps {
  shortlist: ShortlistCandidate[];
  agentContext: AgentContext | null;
  shortlistId: number | null;
  connectionState: ConnectionState;
  onSendMessage: (content: string) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ShortlistPanel({
  shortlist,
  agentContext,
  shortlistId,
  connectionState,
  onSendMessage,
}: ShortlistPanelProps) {
  // Local rating state (optimistic, keyed by person_entity_id)
  const [ratings, setRatings] = useState<Record<number, string | null>>({});

  const handleRate = useCallback(
    (candidate: ShortlistCandidate, newRating: string | null) => {
      // Optimistic update
      setRatings((prev) => ({
        ...prev,
        [candidate.person_entity_id]: newRating,
      }));

      // Persist via API
      if (shortlistId && candidate.item_id != null) {
        updateItem(shortlistId, candidate.item_id, { rating: newRating }).catch(
          (err) => {
            console.warn("Failed to persist rating:", err);
          }
        );
      } else {
        console.warn(
          "Cannot persist rating: missing shortlistId or item_id for candidate",
          candidate.person_entity_id
        );
      }
    },
    [shortlistId]
  );

  // Count rated candidates (from local state or shortlist data)
  const ratedCount = shortlist.filter((c) => {
    const localRating = ratings[c.person_entity_id];
    const effectiveRating =
      localRating !== undefined ? localRating : c.rating ?? null;
    return effectiveRating != null;
  }).length;

  const showRefine = ratedCount >= 2;
  const isStreaming = connectionState !== "idle";

  return (
    <div style={styles.container}>
      {/* Context viewer at top */}
      <ContextViewer context={agentContext} />

      {shortlist.length === 0 ? (
        /* Empty state */
        <div style={styles.emptyState}>
          <MessageSquare
            size={32}
            style={{ color: "var(--text-tertiary)", opacity: 0.3 }}
          />
          <p style={styles.emptyText}>
            Tell me about the role and I&apos;ll find candidates for you
          </p>
        </div>
      ) : (
        /* Candidate list */
        <>
          {/* Refine button */}
          {showRefine && (
            <div style={styles.refineContainer}>
              <button
                type="button"
                disabled={isStreaming}
                onClick={() =>
                  onSendMessage(
                    "Please refine the search based on my ratings"
                  )
                }
                style={{
                  ...styles.refineButton,
                  opacity: isStreaming ? 0.5 : 1,
                  cursor: isStreaming ? "not-allowed" : "pointer",
                }}
              >
                Refine search
              </button>
            </div>
          )}

          {/* Scrollable candidate rows */}
          <div style={styles.candidateList}>
            {shortlist.map((candidate) => {
              const localRating = ratings[candidate.person_entity_id];
              const effectiveRating =
                localRating !== undefined
                  ? localRating
                  : candidate.rating ?? null;

              return (
                <CandidateRow
                  key={candidate.person_entity_id}
                  candidate={candidate}
                  rating={effectiveRating}
                  onRate={(r) => handleRate(candidate, r)}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CandidateRow sub-component
// ---------------------------------------------------------------------------

function CandidateRow({
  candidate,
  rating,
  onRate,
}: {
  candidate: ShortlistCandidate;
  rating: string | null;
  onRate: (rating: string | null) => void;
}) {
  const keywords = (candidate.keywords ?? []).slice(0, 3);

  return (
    <div
      style={styles.row}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.background =
          "color-mix(in srgb, var(--accent-blue) 4%, transparent)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = "transparent";
      }}
    >
      {/* Top line: name + quality badge + rating */}
      <div style={styles.rowTop}>
        <div style={styles.nameArea}>
          <Link
            href={`/profile/${candidate.person_entity_id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.nameLink}
          >
            {candidate.name}
          </Link>
          {candidate.quality_score != null && (
            <span style={styles.qualityBadge}>
              {Math.round(candidate.quality_score)}
            </span>
          )}
        </div>
        <RatingIcons rating={rating} onRate={onRate} />
      </div>

      {/* Headline */}
      {candidate.headline && (
        <p style={styles.headline}>{candidate.headline}</p>
      )}

      {/* Keyword pills */}
      {keywords.length > 0 && (
        <div style={styles.keywordRow}>
          {keywords.map((kw) => (
            <span key={kw} style={styles.keywordPill}>
              {kw}
            </span>
          ))}
        </div>
      )}

      {/* Rationale */}
      {candidate.rationale && (
        <p style={styles.rationale}>{candidate.rationale}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },

  // Empty state
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    gap: "0.75rem",
    padding: "2rem",
  },
  emptyText: {
    color: "var(--text-muted)",
    fontSize: "0.9rem",
    textAlign: "center",
    margin: 0,
    maxWidth: 280,
    lineHeight: 1.4,
  },

  // Refine button
  refineContainer: {
    padding: "0.6rem 1rem",
    borderBottom: "1px solid var(--border)",
  },
  refineButton: {
    width: "100%",
    padding: "0.5rem 1rem",
    fontSize: "0.82rem",
    fontWeight: 600,
    color: "#fff",
    background: "var(--accent-blue)",
    border: "none",
    borderRadius: "8px",
    transition: "opacity 0.15s",
  },

  // Candidate list
  candidateList: {
    flex: 1,
    overflowY: "auto",
  },

  // Row
  row: {
    padding: "0.75rem 1rem",
    borderBottom: "1px solid var(--border)",
    transition: "background 0.12s",
  },
  rowTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "0.5rem",
  },
  nameArea: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    minWidth: 0,
  },
  nameLink: {
    fontWeight: 600,
    color: "var(--accent-blue)",
    textDecoration: "none",
    fontSize: "0.9rem",
  },
  qualityBadge: {
    fontSize: "0.68rem",
    fontWeight: 600,
    padding: "0.1rem 0.35rem",
    borderRadius: "6px",
    color: "var(--text-secondary)",
    background: "var(--surface-elevated)",
    border: "1px solid var(--border)",
    whiteSpace: "nowrap",
  },

  // Headline
  headline: {
    fontSize: "0.82rem",
    color: "var(--text-secondary)",
    margin: "0.15rem 0 0",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  // Keywords
  keywordRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.3rem",
    marginTop: "0.35rem",
  },
  keywordPill: {
    borderRadius: "12px",
    padding: "0.12rem 0.45rem",
    fontSize: "0.73rem",
    color: "color-mix(in srgb, var(--accent-blue) 80%, #fff 20%)",
    border:
      "1px solid color-mix(in srgb, var(--accent-blue) 44%, var(--border) 56%)",
    background:
      "color-mix(in srgb, var(--accent-blue) 18%, var(--surface-elevated) 82%)",
  },

  // Rationale
  rationale: {
    fontSize: "0.78rem",
    color: "var(--text-tertiary)",
    margin: "0.3rem 0 0",
    lineHeight: 1.35,
  },
};
